import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader } from 'rsuite';

import BasicPanel from '../../components/panels/basicPanel/basicPanel';
import BasicWrapper from '../../components/basicWrapper/basicWrapper';
import { useMessenger } from '../../context/messengerContext';
import { getProfile } from '../../api/user';
import Btn from '../../components/Btn/Btn';

import './ProfilePage.css';
import { isIOSVersionHigherThan26Version } from '../../utils/getUserAgentParser';

export default function ProfilePage() {
    const { t } = useTranslation('basic');
    const { userId, platform, user } = useMessenger();
    const navigate = useNavigate();

    const [profile, setProfile] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(null);

    const isIOS26Version = isIOSVersionHigherThan26Version();

    useEffect(() => {
        const loadProfile = async () => {
            setIsLoading(true);
            setIsError(null);
            try {
                const res = await getProfile({ platform, userId });
                setProfile(res);
            } catch (err) {
                setIsError(err?.message || t('profile_error'));
            } finally {
                setIsLoading(false);
            }
        };
        loadProfile();
    }, [userId]);

    if (isLoading) {
        return (
            <div className={`loader-container ${isIOS26Version ? 'ios26_top-safe-area' : ''}`}>
                <Loader size='lg' />
            </div>
        );
    }

    if (isError) {
        return (
            <div className={`profilePage ${isIOS26Version ? 'ios26_top-safe-area' : ''}`}>
                <BasicPanel>
                    <p className='profilePage_error'>{isError}</p>
                </BasicPanel>
            </div>
        );
    }

    const buildFullName = (obj) =>
        [obj?.last_name, obj?.first_name, obj?.middle_name].filter(Boolean).join(' ');

    const displayName =
        buildFullName(profile) ||
        buildFullName(user) ||
        '';
    const avatarLetter = (displayName?.[0] ?? '?').toUpperCase();
    const contracts = Array.isArray(profile?.contracts) ? profile.contracts : [];
    return (
        <div className={`stepsHeaderWrapper gradient_bg_header profilePage ${isIOS26Version ? 'ios26_top-safe-area' : ''}`}>
            <BasicWrapper>
                <div className='contractsList_header'>
                    <div className='stepHeaderHeader heading-h4'>
                        {t('profile_title')}
                        <img
                            id='stepsHeaderImage'
                            src='https://s3.timeweb.com/8a6e091b-publick/template_header.png'
                            alt=''
                        />
                    </div>
                </div>

                <div className='profilePage_content'>
                    {user?.photoUrl ? (
                        <img
                            className='profilePage_avatar'
                            src={user.photoUrl}
                            alt={displayName}
                        />
                    ) : (
                        <div className='profilePage_avatarPlaceholder'>
                            {avatarLetter}
                        </div>
                    )}

                    <div className='profilePage_fields'>
                        <div className='profilePage_card'>
                            <div className='profilePage_cardRow'>
                                {t('profile_name')}:{' '}
                                <span className='profilePage_cardRowValue'>
                                    {displayName || '—'}
                                </span>
                            </div>

                            {platform !== 'web' && user?.username && (
                                <div className='profilePage_cardRow'>
                                    {t('profile_username')}:{' '}
                                    <span className='profilePage_cardRowValue'>
                                        @{user.username}
                                    </span>
                                </div>
                            )}
                        </div>

                        <div className='profilePage_card'>
                            {contracts.length > 0 ? (
                                contracts.map((c) => {
                                    const contractNum = typeof c === 'string' ? c : c.contract_number;
                                    return (
                                        <div key={contractNum} className='profilePage_cardRow'>
                                            {t('profile_contract')}:{' '}
                                            <span className='profilePage_cardRowValue'>
                                                {contractNum}
                                            </span>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className='profilePage_cardRow'>
                                    {t('profile_no_contracts')}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className='profilePage_actions'>
                        <Btn
                            text={t('profile_edit')}
                            onClick={() => navigate('/profile-update')}
                        />
                    </div>
                </div>
            </BasicWrapper>
        </div>
    );
}
